import React, { useState, useEffect } from 'react';
import { referralAPI } from '../lib/api';
import { Link } from 'react-router-dom';
import { Trophy, Medal, Crown, Diamond, ArrowLeft, Users, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import PageMeta from '../components/PageMeta';

const BADGE_CONFIG = {
  diamond: { label: 'Diamond', color: 'from-[#B9F2FF] to-[#62D4EE]', textColor: 'text-[#0C7C9E]', border: 'border-[#62D4EE]', icon: <Diamond className="w-4 h-4" /> },
  gold: { label: 'Gold', color: 'from-[#FFE082] to-[#FFB300]', textColor: 'text-[#7A5600]', border: 'border-[#FFB300]', icon: <Crown className="w-4 h-4" /> },
  silver: { label: 'Silver', color: 'from-[#E0E0E0] to-[#9E9E9E]', textColor: 'text-[#424242]', border: 'border-[#9E9E9E]', icon: <Medal className="w-4 h-4" /> },
  bronze: { label: 'Bronze', color: 'from-[#FFCCBC] to-[#E65100]', textColor: 'text-[#BF360C]', border: 'border-[#E65100]', icon: <Medal className="w-4 h-4" /> },
};

const RANK_STYLES = [
  'bg-gradient-to-r from-[#FFE082]/30 to-transparent border-l-4 border-l-[#FFB300]',
  'bg-gradient-to-r from-[#E0E0E0]/30 to-transparent border-l-4 border-l-[#9E9E9E]',
  'bg-gradient-to-r from-[#FFCCBC]/30 to-transparent border-l-4 border-l-[#E65100]',
];

const LeaderboardPage = () => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [thresholds, setThresholds] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await referralAPI.getLeaderboard();
        setLeaderboard(res.data.leaderboard || []);
        setThresholds(res.data.badge_thresholds || {});
      } catch { toast.error('Failed to load leaderboard'); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <PageMeta title="Leaderboard - GMB Autopilot" />
      <div className="bg-[#202124] text-white">
        <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#9AA0A6] hover:text-white mb-6 transition-colors" data-testid="back-to-home">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-[#FFB300] to-[#FF6F00] rounded-2xl flex items-center justify-center shadow-lg">
              <Trophy className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: 'Outfit' }}>Referral Leaderboard</h1>
              <p className="text-[#9AA0A6] mt-1">Top referrers who are growing our community</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Badge Tiers Info */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="badge-tiers">
          {['bronze', 'silver', 'gold', 'diamond'].map(tier => {
            const cfg = BADGE_CONFIG[tier];
            return (
              <div key={tier} className={`rounded-xl border-2 ${cfg.border} p-3 text-center bg-white`}>
                <div className={`w-10 h-10 mx-auto rounded-full bg-gradient-to-br ${cfg.color} flex items-center justify-center mb-2`}>
                  {cfg.icon}
                </div>
                <p className={`text-sm font-semibold ${cfg.textColor}`}>{cfg.label}</p>
                <p className="text-xs text-[#5F6368]">{thresholds[tier] || 0}+ referrals</p>
              </div>
            );
          })}
        </div>

        {/* Leaderboard Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-[#DADCE0] overflow-hidden" data-testid="leaderboard-table">
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin w-8 h-8 border-2 border-[#1A73E8] border-t-transparent rounded-full" />
            </div>
          ) : leaderboard.length === 0 ? (
            <div className="text-center py-16">
              <Users className="w-12 h-12 text-[#DADCE0] mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-[#202124] mb-1" style={{ fontFamily: 'Outfit' }}>No referrers yet</h3>
              <p className="text-sm text-[#5F6368]">Be the first to refer and climb the leaderboard!</p>
              <Link to="/register" className="mt-4 inline-flex items-center gap-2 bg-[#1A73E8] text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-[#1557B0] transition-colors" data-testid="join-now-btn">
                Join Now
              </Link>
            </div>
          ) : (
            <div>
              {/* Table Header */}
              <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-[#F8F9FA] border-b border-[#DADCE0] text-xs font-medium text-[#5F6368]">
                <div className="col-span-1 text-center">#</div>
                <div className="col-span-4">Referrer</div>
                <div className="col-span-2 text-center">Referrals</div>
                <div className="col-span-2 text-center">Active</div>
                <div className="col-span-3 text-right">Earnings</div>
              </div>
              {/* Rows */}
              {leaderboard.map((entry, idx) => {
                const badge = entry.badge ? BADGE_CONFIG[entry.badge] : null;
                const rankStyle = idx < 3 ? RANK_STYLES[idx] : '';
                return (
                  <div
                    key={idx}
                    className={`grid grid-cols-12 gap-2 px-4 py-3 items-center border-b border-[#F1F3F4] last:border-b-0 transition-colors hover:bg-[#F8F9FA] ${rankStyle}`}
                    data-testid={`leaderboard-row-${idx}`}
                  >
                    {/* Rank */}
                    <div className="col-span-1 text-center">
                      {idx === 0 ? (
                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gradient-to-br from-[#FFE082] to-[#FFB300] text-[#7A5600] text-xs font-bold">1</span>
                      ) : idx === 1 ? (
                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gradient-to-br from-[#E0E0E0] to-[#9E9E9E] text-[#424242] text-xs font-bold">2</span>
                      ) : idx === 2 ? (
                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gradient-to-br from-[#FFCCBC] to-[#E65100] text-white text-xs font-bold">3</span>
                      ) : (
                        <span className="text-sm text-[#5F6368] font-medium">{idx + 1}</span>
                      )}
                    </div>
                    {/* Name + Badge */}
                    <div className="col-span-4 flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-[#E8F0FE] flex items-center justify-center text-xs font-semibold text-[#1A73E8] flex-shrink-0">
                        {entry.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-[#202124] truncate">{entry.name}</p>
                        {badge && (
                          <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-gradient-to-r ${badge.color} ${badge.textColor}`}>
                            {badge.icon} {badge.label}
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Referrals */}
                    <div className="col-span-2 text-center">
                      <span className="text-sm font-semibold text-[#202124]">{entry.referral_count}</span>
                    </div>
                    {/* Active */}
                    <div className="col-span-2 text-center">
                      <span className="text-sm text-[#1E8E3E] font-medium">{entry.active_customers}</span>
                    </div>
                    {/* Earnings */}
                    <div className="col-span-3 text-right">
                      {entry.total_earned_inr > 0 && <p className="text-sm font-semibold text-[#202124]">₹{entry.total_earned_inr.toFixed(2)}</p>}
                      {entry.total_earned_usd > 0 && <p className="text-sm font-semibold text-[#202124]">${entry.total_earned_usd.toFixed(2)}</p>}
                      {!entry.total_earned_inr && !entry.total_earned_usd && <p className="text-xs text-[#9AA0A6]">—</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* CTA */}
        <div className="bg-gradient-to-r from-[#1A73E8] to-[#0D47A1] rounded-2xl p-6 text-center text-white" data-testid="leaderboard-cta">
          <TrendingUp className="w-8 h-8 mx-auto mb-3 opacity-80" />
          <h3 className="text-lg font-semibold mb-1" style={{ fontFamily: 'Outfit' }}>Join the Referral Program</h3>
          <p className="text-sm text-white/80 mb-4 max-w-md mx-auto">
            Share your referral link, earn commissions on every signup, and climb the leaderboard to unlock badges!
          </p>
          <Link to="/register" className="inline-flex items-center gap-2 bg-white text-[#1A73E8] px-6 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#F8F9FA] transition-colors" data-testid="leaderboard-register-btn">
            Get Started Free
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LeaderboardPage;
